/*
 * Homepage V3 -- scroll-snap carousel.
 *
 * Progressive enhancement: the track is already a horizontally scrollable
 * scroll-snap list in CSS, so it works by swipe with JavaScript disabled.
 * This adds previous/next buttons and page dots on top of that.
 *
 * Pages are derived from how many cards actually fit, so the controls stay
 * correct when the merchant changes "cards per view" or the viewport
 * crosses a breakpoint.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-hv3-carousel]';

  function Carousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-hv3-carousel-track]');
    this.dots = root.querySelector('[data-hv3-carousel-dots]');
    this.prev = root.querySelector('[data-hv3-carousel-prev]');
    this.next = root.querySelector('[data-hv3-carousel-next]');

    if (!this.track) return;

    this.items = Array.prototype.slice.call(this.track.children);
    if (!this.items.length) return;

    this.page = 0;
    this.bind();
    this.refresh();
  }

  // How many whole cards fit in the visible track, at least one.
  Carousel.prototype.perPage = function () {
    var first = this.items[0];
    if (!first) return 1;
    var itemWidth = first.getBoundingClientRect().width;
    if (!itemWidth) return 1;
    var gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    var fit = Math.round(
      (this.track.clientWidth + gap) / (itemWidth + gap)
    );
    return Math.max(1, Math.min(fit, this.items.length));
  };

  Carousel.prototype.pageCount = function () {
    return Math.max(1, Math.ceil(this.items.length / this.perPage()));
  };

  Carousel.prototype.bind = function () {
    var self = this;

    if (this.prev) {
      this.prev.addEventListener('click', function () {
        self.go(self.page - 1);
      });
    }

    if (this.next) {
      this.next.addEventListener('click', function () {
        self.go(self.page + 1);
      });
    }

    // Keep the dots and disabled states honest when the user swipes.
    var raf = null;
    this.track.addEventListener(
      'scroll',
      function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          self.syncFromScroll();
        });
      },
      { passive: true }
    );

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        self.refresh();
      }, 150);
    });
  };

  Carousel.prototype.syncFromScroll = function () {
    var perPage = this.perPage();
    var first = this.items[0];
    if (!first) return;
    var gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    var step = (first.getBoundingClientRect().width + gap) * perPage;
    if (!step) return;
    var page = Math.round(this.track.scrollLeft / step);
    if (page !== this.page) {
      this.page = page;
      this.update();
    }
  };

  Carousel.prototype.go = function (page) {
    var last = this.pageCount() - 1;
    this.page = Math.max(0, Math.min(page, last));

    var perPage = this.perPage();
    var target = this.items[this.page * perPage];
    if (!target) return;

    this.track.scrollTo({
      left: target.offsetLeft - this.track.offsetLeft,
      behavior: 'smooth'
    });

    this.update();
  };

  Carousel.prototype.update = function () {
    var count = this.pageCount();
    var last = count - 1;

    if (this.prev) this.prev.disabled = this.page <= 0;
    if (this.next) this.next.disabled = this.page >= last;

    // A single page means nothing to page through.
    this.root.classList.toggle('hv3-carousel--static', count <= 1);

    if (!this.dots) return;
    var children = this.dots.children;
    for (var i = 0; i < children.length; i++) {
      children[i].classList.toggle('is-active', i === this.page);
    }
  };

  Carousel.prototype.refresh = function () {
    if (this.dots) {
      var count = this.pageCount();
      this.dots.innerHTML = '';
      for (var i = 0; i < count; i++) {
        var dot = document.createElement('span');
        dot.className = 'hv3-reviews__dot';
        this.dots.appendChild(dot);
      }
    }
    this.page = Math.min(this.page, this.pageCount() - 1);
    this.update();
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll(SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.hv3CarouselReady) return;
      root.dataset.hv3CarouselReady = 'true';
      new Carousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Re-initialise when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
